import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FaPlay, FaPause, FaExpand } from 'react-icons/fa';
import { glassDeep } from '../../../styles/components/common/glassmorphism';
import { useAuth } from '../../../contexts/AuthContext';

const VideoCardContainer = styled.div`
  ${glassDeep}
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  margin-bottom: 1.5rem;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
    border-color: rgba(99, 102, 241, 0.3);
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const CardHeader = styled.div`
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  h3 {
    margin: 0;
    font-size: 1.25rem;
    color: rgba(255, 255, 255, 0.9);
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.8));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 600;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  overflow: hidden;
  box-shadow: inset 0 0 40px rgba(0, 0, 0, 0.2);
  background: #1f2937;
`;

const Video = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: none; /* Hide video element since we're not using it */
`;

const VideoControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  opacity: ${({ isPlaying }) => (isPlaying ? '0' : '1')};
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1;
  }
  
  @media (max-width: 576px) {
    padding: 0.75rem;
  }
`;

const ControlButton = styled.button`
  background: rgba(99, 102, 241, 0.6);
  color: white;
  border: none;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);
  
  &:hover {
    background: rgba(99, 102, 241, 0.8);
    transform: scale(1.05);
    box-shadow: 0 0 15px rgba(99, 102, 241, 0.5);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 576px) {
    width: 2rem;
    height: 2rem;
    font-size: 0.875rem;
  }
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 5px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2.5px;
  margin: 0 1rem;
  position: relative;
  cursor: pointer;
  
  &:hover::before {
    transform: scale(1.3);
  }
`;

const Progress = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${({ progress }) => `${progress}%`};
  background: linear-gradient(90deg, rgba(124, 58, 237, 0.8), rgba(99, 102, 241, 0.8));
  border-radius: 2.5px;
  
  &::before {
    content: '';
    position: absolute;
    right: -5px;
    top: -5px;
    width: 15px;
    height: 15px;
    background: white;
    border-radius: 50%;
    box-shadow: 0 0 10px rgba(99, 102, 241, 0.8);
    transition: transform 0.2s ease;
  }
`;

const Description = styled.div`
  padding: 1rem 1.5rem;
  
  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.95rem;
    line-height: 1.6;
  }
  
  @media (max-width: 576px) {
    padding: 1rem;
    
    p {
      font-size: 0.875rem;
    }
  }
`;

const InternshipGuidelinesVideo = ({ defaultMajor, studentType }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const { user } = useAuth();
  
  // Get major from user context or use default
  const [userMajor, setUserMajor] = useState(defaultMajor || 'Computer Science');
  
  useEffect(() => {
    // In a real app, this would be retrieved from the user's profile
    // For now, we'll just use the defaultMajor or a fallback
    if (user) {
      // Simulate fetching the major from the user profile
      // In the real app, you'd get this from the user profile
      setUserMajor(defaultMajor || 'Computer Science');
    }
  }, [user, defaultMajor]);
  
  // Determine video title and description based on major
  const getVideoDetails = () => {
    // You can customize these based on each major
    switch(userMajor) {
      case 'Computer Science':
        return {
          title: 'Computer Science Internship Guidelines',
          description: 'Learn about the types of internships that count towards your Computer Science degree. Software development, data science, AI/ML, and cybersecurity internships are typically accepted.'
        };
      case 'Electrical Engineering':
        return {
          title: 'Electrical Engineering Internship Guidelines',
          description: 'Discover which internships fulfill your Electrical Engineering requirements. Hardware design, IoT development, robotics, and control systems internships are generally accepted.'
        };
      case 'Business Administration':
        return {
          title: 'Business Administration Internship Guidelines',
          description: 'Find out about qualifying internships for Business Administration students. Management, marketing, finance, and consulting positions typically count toward your internship requirement.'
        };
      default:
        return {
          title: 'Internship Guidelines',
          description: 'Learn about the types of internships that count towards your degree requirements. This video provides guidance on selecting appropriate internships based on your major.'
        };
    }
  };
  
  const { title, description } = getVideoDetails();

  const handlePlayPause = () => {
    // Toggle the isPlaying state for visual feedback
    setIsPlaying(!isPlaying);
    
    // Show a message to the user about the guidelines
    if (!isPlaying) {
      // Optional: You could add a toast notification here in a real implementation
      console.log(`Showing internship guidelines for ${userMajor}`);
    }
  };
  
  const handleFullScreen = () => {
    // Access the VideoContainer element using a ref to enter fullscreen
    const container = document.querySelector('.video-container');
    if (container) {
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
      } else if (container.msRequestFullscreen) {
        container.msRequestFullscreen();
      }
    }
  };
  
  useEffect(() => {
    // No need for complex event listeners since we don't have actual video playing
    return () => {
      // Cleanup
    };
  }, []);
  
  const [videoError, setVideoError] = useState(false);
  
  return (
    <VideoCardContainer>
      <CardHeader>
        <h3>{title}</h3>
      </CardHeader>
      
      <VideoContainer className="video-container">
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: isPlaying 
              ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(23, 33, 64, 0.95))' 
              : 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(23, 33, 64, 0.9))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='rgba(255,255,255,0.05)' fill-rule='evenodd'/%3E%3C/svg%3E")`,
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onClick={handlePlayPause}
        >
          <div style={{ 
            color: 'white', 
            textAlign: 'center', 
            padding: '1rem',
            fontSize: '1.25rem',
            fontWeight: '500',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <div style={{
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              background: 'rgba(99, 102, 241, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.8rem',
              boxShadow: '0 0 25px rgba(99, 102, 241, 0.5), 0 0 15px rgba(255, 255, 255, 0.2)',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              transition: 'all 0.3s ease',
              transform: isPlaying ? 'scale(0.9)' : 'scale(1)'
            }}
            onClick={() => handlePlayPause()}>
              {isPlaying ? <FaPause /> : <FaPlay style={{ marginLeft: '4px' }} />}
            </div>
            <p>{isPlaying ? 
              `Now showing ${userMajor} internship guidelines` : 
              `Click to view internship requirements for ${userMajor}`}</p>
          </div>
        </div>
        
        <VideoControls isPlaying={isPlaying}>
          <ControlButton onClick={handlePlayPause}>
            {isPlaying ? <FaPause /> : <FaPlay />}
          </ControlButton>
          
          <div style={{ flex: 1 }}></div>
          
          <ControlButton onClick={handleFullScreen}>
            <FaExpand />
          </ControlButton>
        </VideoControls>
      </VideoContainer>
      
      <Description>
        <p>{description}</p>
      </Description>
    </VideoCardContainer>
  );
};

export default InternshipGuidelinesVideo;
